import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosRequestConfig,
} from 'axios';

import { getConfig } from '@app/config';

import { tokenService } from '@common/services/tokenStore';
import { toQueryString } from '@common/utils/toQueryString';

function reloadApp() {
  console.log('[Auth] Token expired - reloading app');
  window.location.reload();
}

const getAuthorizationHeaders = () => {
  const authHeader = getAuthHeader();

  if (!authHeader) {
    return undefined;
  }

  return { Authorization: authHeader };
};

// Endpoints that intentionally do not require a Bearer token (they are gated
// by tmaInitData / public). A 401 on these paths must NOT trigger a full-page
// reload — that would either loop (if the cause is a stale tmaInitData) or
// drop in-flight user state for an analytics no-op.
//
// Mirror this list against server/routes/api/v1/index.js when adding new
// public endpoints. Order doesn't matter; lookup is by exact path.
const PUBLIC_ENDPOINT_PATHS: ReadonlySet<string> = new Set([
  '/sign-in/tma',
  '/sign-in/google',
  '/sign-in/apple',
  '/register/tma',
  '/register/google',
  '/register/apple',
  '/register/mobile-app-tg-auth',
  '/register/mobile-app-vk-auth',
  '/register/mobile-app-google-auth',
  '/register/mobile-app-apple-auth',
  '/start/tma',
  '/start/android',
  '/start/ios',
  '/tracking/utm-session',
  '/tracking/ym-goal',
  '/client-errors',
  '/matchmaker/invites/open',
]);

export const PRE_AUTH_PATHS: ReadonlySet<string> = new Set([
  '/sign-up',
  '/invite',
]);

export const isPreAuthPath = (pathname: string) =>
  PRE_AUTH_PATHS.has(pathname.replace(/\/+$/, '') || '/');

// Resolve the request path from axios's stored `error.config.url`.
// In our codebase fetcher always passes a relative URL via baseURL, so
// error.config.url is normally something like "/sign-in/tma". Handle absolute
// URLs and an optional /api/v<N>/ prefix defensively in case future call
// sites differ. Returns null when the URL cannot be parsed.
function extractPathname(rawUrl: string | undefined): string | null {
  if (!rawUrl) return null;
  // Strip query string before any further parsing.
  const queryIdx = rawUrl.indexOf('?');
  const noQuery = queryIdx === -1 ? rawUrl : rawUrl.slice(0, queryIdx);
  let pathname = noQuery;
  if (/^https?:\/\//i.test(noQuery)) {
    try {
      pathname = new URL(noQuery).pathname;
    } catch {
      return null;
    }
  }
  // Drop /api/v<N> prefix so absolute and relative URLs normalize to the
  // same form against the allowlist (which uses the relative form).
  return pathname.replace(/^\/api\/v\d+/, '') || pathname;
}

function isPublicEndpoint(rawUrl: string | undefined): boolean {
  const path = extractPathname(rawUrl);
  return path !== null && PUBLIC_ENDPOINT_PATHS.has(path);
}

// Setup axios interceptor to handle expired tokens
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const isPreAuthPage = isPreAuthPath(window.location.pathname);

    if (error.response?.status === 419) {
      reloadApp();
    }

    if (
      error.response?.status === 401 &&
      !isPublicEndpoint(error.config?.url) &&
      !isPreAuthPage
    ) {
      reloadApp();
    }

    return Promise.reject(error);
  },
);

export const postEvent = async (
  event_type: string,
  metadata?: Record<string, unknown>,
  first_only?: boolean,
) => {
  try {
    const cfg = await getConfig();
    const authHeaders = getAuthorizationHeaders();

    if (!authHeaders) {
      return;
    }

    await axios.post(
      '/events',
      {
        event_type,
        metadata,
        first_only,
      },
      {
        baseURL: cfg.api_url,
        headers: authHeaders,
      },
    );
  } catch (e) {
    // swallow client-side tracking errors
  }
};

interface SuccessResponse<T> {
  status: 'success';
  message: string;
  data: T;
}

interface ErrorResponse {
  status: 'error';
  message: string;
  // Values are not all strings: billing sends `currentPeriodEndAt: null` and
  // zod validation sends string[].
  errors: Record<string, unknown>;
}

interface MaintenanceResponse {
  status: 'maintenance';
  message?: string;
  revision?: string;
}

export class APIError extends Error {
  status: 'error';
  message: string;
  errors: Record<string, unknown>;

  constructor(data: ErrorResponse) {
    super(data.message);
    this.status = data.status;
    this.message = data.message;
    this.errors = data.errors;
  }
}

/**
 * A 2xx whose body is not the API's JSON envelope, such as the SPA's
 * index.html that the server's static catch-all answers for a GET under
 * /api/v1 it has no route for (an older backend). It stays an APIError, so
 * callers that branch on `instanceof APIError` see no change.
 */
export class UnexpectedResponseError extends APIError {
  constructor() {
    super({
      status: 'error',
      message: 'The response is not an API answer',
      errors: {},
    });
    this.name = 'UnexpectedResponseError';
  }
}

export const isUnexpectedResponseError = (
  error: unknown,
): error is UnexpectedResponseError => error instanceof UnexpectedResponseError;

export class MaintenanceError extends Error {
  status = 'maintenance' as const;
  revision?: string;

  constructor(data: MaintenanceResponse) {
    super(
      data.message || 'Service is temporarily unavailable for maintenance.',
    );
    this.name = 'MaintenanceError';
    this.revision = data.revision;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isMaintenanceResponse(data: unknown): data is MaintenanceResponse {
  if (!isRecord(data)) {
    return false;
  }

  return (
    data.status === 'maintenance' &&
    (data.message === undefined || typeof data.message === 'string') &&
    (data.revision === undefined || typeof data.revision === 'string')
  );
}

export function isMaintenanceError(error: unknown): error is MaintenanceError {
  return error instanceof MaintenanceError;
}

/**
 * Reads the server's stable `errors.code` (and the rest of the `errors`
 * payload) off a failed request, so callers can branch on a code instead of
 * matching message text.
 *
 * Two shapes reach callers. axios rejects any non-2xx, and `fetcher`'s catch
 * rethrows everything except 503, so an error status arrives as a raw
 * AxiosError and `status` is populated. `fetcher` only constructs an APIError
 * for a 2xx whose body carries `status: 'error'`; there is no HTTP status to
 * report for that, so `status` is null. Prefer branching on `code` alone —
 * it is specific enough — and treat `status` as extra context.
 */
export function getApiErrorInfo(error: unknown): {
  status: number | null;
  code: string | null;
  details: Record<string, unknown> | null;
} {
  if (error instanceof APIError) {
    const code = error.errors?.code;

    return {
      status: null,
      code: typeof code === 'string' ? code : null,
      details: isRecord(error.errors) ? error.errors : null,
    };
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const details =
      isRecord(data) && isRecord(data.errors) ? data.errors : null;
    const code = details?.code;

    return {
      status: error.response?.status ?? null,
      code: typeof code === 'string' ? code : null,
      details,
    };
  }

  return { status: null, code: null, details: null };
}

/**
 * `errors.code` of the server's request rate limits (HTTP 429). Another 429
 * without it, such as the daily swipe limit, means something else.
 */
export const RATE_LIMITED_ERROR_CODE = 'RATE_LIMITED';

export const isRateLimitedError = (error: unknown) =>
  getApiErrorInfo(error).code === RATE_LIMITED_ERROR_CODE;

/** A response header, by its name in lower case, from either header shape. */
const readResponseHeader = (headers: unknown, name: string) => {
  if (headers instanceof AxiosHeaders) return headers.get(name);
  if (!isRecord(headers)) return undefined;

  const key = Object.keys(headers).find((each) => each.toLowerCase() === name);

  return key === undefined ? undefined : headers[key];
};

/** A Retry-After value, delay-seconds or an HTTP date, in ms from `now`. */
const parseRetryAfterHeader = (value: unknown, now: number) => {
  if (typeof value !== 'string') return null;

  const text = value.trim();

  if (/^\d+$/.test(text)) return Number(text) * 1000;

  const date = Date.parse(text);

  return Number.isNaN(date) ? null : date - now;
};

/** The server's wait, uncapped: the header first, then the body. */
const readRetryAfterMs = (error: unknown, now: number) => {
  const headers = axios.isAxiosError(error)
    ? error.response?.headers
    : undefined;
  const fromHeader = parseRetryAfterHeader(
    readResponseHeader(headers, 'retry-after'),
    now,
  );

  if (fromHeader !== null && fromHeader > 0) return fromHeader;

  const fromBody = getApiErrorInfo(error).details?.retryAfter;

  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0)
    return fromBody * 1000;

  return null;
};

// The rate limits' longest window. It also keeps a wait below setTimeout's
// 2^31-1 ms, past which the timer would fire at once.
const RETRY_AFTER_MAX_MS = 60 * 60 * 1000;

/**
 * How long the server asked to wait before the next request, in ms (at most
 * an hour), or null when it did not say. The Retry-After header comes first;
 * the rate limits also send the seconds as `errors.retryAfter`, which is what
 * a browser sees when the API is on another origin: the API does not list
 * Retry-After in Access-Control-Expose-Headers, so the header is hidden there.
 */
export function getRetryAfterMs(
  error: unknown,
  now = Date.now(),
): number | null {
  const retryAfterMs = readRetryAfterMs(error, now);

  return retryAfterMs === null
    ? null
    : Math.min(retryAfterMs, RETRY_AFTER_MAX_MS);
}

type Props = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  params?: AxiosRequestConfig['params'];
  auth?: 'bearer' | null;
  /**
   * Gives up after this many ms: the request is aborted and the call rejects
   * with an ECONNABORTED AxiosError, as axios's own timeout does. Without
   * it, a request waits as long as the connection does.
   */
  timeout?: number;
};

/**
 * The request, or an ECONNABORTED AxiosError once `timeoutMs` has passed.
 * The deadline is kept here rather than in axios's `timeout`, so it holds
 * whatever adapter sends the request.
 */
const withDeadline = <R>(
  request: Promise<R>,
  timeoutMs: number,
  controller: AbortController,
) =>
  new Promise<R>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(
        new AxiosError(
          `timeout of ${timeoutMs}ms exceeded`,
          AxiosError.ECONNABORTED,
        ),
      );
    }, timeoutMs);

    request.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

export const fetcher = async <T = unknown>({
  url,
  method = 'GET',
  data,
  params,
  auth = 'bearer',
  timeout,
}: Props) => {
  const config = await getConfig();
  const authHeaders = auth === null ? undefined : getAuthorizationHeaders();
  const controller = timeout ? new AbortController() : null;
  const request = axios<SuccessResponse<T> | ErrorResponse>({
    baseURL: config.api_url,
    url,
    method,
    data,
    params,
    paramsSerializer: toQueryString,
    ...(authHeaders ? { headers: authHeaders } : {}),
    ...(controller ? { signal: controller.signal } : {}),
  });
  const response = await (
    timeout && controller ? withDeadline(request, timeout, controller) : request
  ).catch((e: AxiosError) => {
    if (e.response?.status === 503 && isMaintenanceResponse(e.response.data)) {
      throw new MaintenanceError(e.response.data);
    }

    throw e;
  });

  const responseData = response.data;

  if (!isRecord(responseData)) {
    throw new UnexpectedResponseError();
  }

  if (responseData.status === 'success') {
    return responseData.data;
  } else {
    throw new APIError(responseData);
  }
};

export const getAuthHeader = () => {
  const token = tokenService.getToken();

  if (!token) {
    return null;
  }

  return `Bearer ${token}`;
};

export type CommonCursorResponse = {
  limit: number;
  hasMore: boolean;
  nextCursor: string;
};
